import React, { FC, memo, useMemo } from 'react';
import { Colors, FInter, FlexBox, ExternalLink } from '@brg/ui';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { Project } from '@brg/api';
import { CreateLinkTo } from '../../../constants/linkTo';
import { LayoutConst } from "../../../../core";
import { AppTester } from 'supporting';

const ProjectDesignerWrapper = styled(FInter.Text) <{ notLink?: boolean, alwaysBlack?: boolean }>`
  font-size: inherit;
  line-height: inherit;
  cursor: ${p => p?.notLink ? 'default' : 'pointer'};
  transition: all 0.2s;
  font-weight: 600;
  color: ${({ theme }) => theme.projectView.authorText};

  ${({ notLink, alwaysBlack, theme }) => {
    if (!notLink) {
      return css`
        &:hover > a {
          color: ${alwaysBlack ? Colors.galBlack100 : theme.projectView.authorColorHover} !important;
        }
      `
    }
  }};
`;

export const ProjectHeaderRoot = styled(FlexBox) <{ isProjectPage?: boolean, isBookPage?: boolean }>`
  display: block;
  margin-bottom: 16px;
  font-size: ${({ theme }) => theme.navigationBar.font.m.fontSize};
  line-height: ${({ theme }) => theme.navigationBar.font.m.lineHeight};
  white-space: break-spaces;

  a {
    color: ${({ theme }) => theme.projectView.authorText};
    transition: color 0.2s;

    &:hover {
      color: ${(p) => p.theme.projectView.authorColorHover};
      transition: color 0.2s;
    }
  }

  @media (max-width: ${LayoutConst.screen.l_16}px) {
    font-size: ${({ theme }) => theme.navigationBar.font.m.fontSize};
    line-height: ${({ theme }) => theme.navigationBar.font.m.lineHeight};

    .ProjectHeaderMadeBy {
      font-size: ${({ theme }) => theme.navigationBar.font.m.fontSize};
      line-height: ${({ theme }) => theme.navigationBar.font.m.lineHeight};
    }
  }

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: ${({ theme }) => theme.navigationBar.font.s.fontSize};
    line-height: ${({ theme }) => theme.navigationBar.font.s.lineHeight};

    .ProjectHeaderMadeBy {
      font-size: ${({ theme }) => theme.navigationBar.font.s.fontSize};
      line-height: ${({ theme }) => theme.navigationBar.font.s.lineHeight};
    }
  }

  ${({ isBookPage }) => isBookPage ? css`
    @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
      max-width: 888px;
      margin: 0 auto 16px auto;
      width: 100%;
    }

    @media (max-width: ${LayoutConst.screen.l_12}px) {
      max-width: 100%;
    }
  ` : null};

  @media (max-width: 940px) {
    margin-bottom: 0;
    // margin-top: 16px;
    padding: 0 16px 0 0;

    * {
      font-weight: 700;
      font-size: 13px;
      line-height: 16px;
    }

    .ProjectHeaderMadeBy {
      font-size: 13px;
      line-height: 16px;
    }
  }

  ${({ isProjectPage }) => isProjectPage ? css`
    @media (max-width: ${LayoutConst.screen.md}px) {
      padding: 0 16px 0 16px;
    }

    @media (max-width: ${LayoutConst.screen.s - 1}px) {
      padding: 0 10px 0 0;
      padding-top: 0;
    }
  ` : null};

  span {
    @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
      font-size: inherit;
      line-height: inherit;
    }

    @media (max-width: 940px) {
      font-size: inherit;
      line-height: inherit;
    }
  }

  .ProjectHeaderMadeByOuter {
    margin-left: 5px;
  }
`;

const MadeBy = styled(FInter.Text)`
  font-size: ${({ theme }) => theme.navigationBar.font.m.fontSize};
  line-height: ${({ theme }) => theme.navigationBar.font.m.lineHeight};
  margin-right: 5px;
  color: ${({ theme }) => theme.projectView.authorCuratorText};
`;

type PropsT = {
  project: Project;
  fireClick?: boolean;
  isProjectPage?: boolean;
  isBookPage?: boolean;
  variant?: string;
};

const ProjectHeader: FC<PropsT> = ({ project, fireClick, isProjectPage, isBookPage, variant }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const isOnlines = AppTester.isOnlines();

  const authorsList = useMemo(
    () => {
      const authors = (project?.author ? [project?.author] : []).concat(project?.coAuthors || []);
      return (authors).map((author, index) => (
        <ProjectDesignerWrapper
          key={author.designerId}
          notLink={isOnlines || fireClick}
          alwaysBlack={variant === "bottom"}
          itemProp="author" itemScope itemType="https://schema.org/Person"
        >
          {(isOnlines || fireClick) ? (
            <span itemProp="name">
              {`${author.name || 'unnamed'} ${author.surName || 'user'}`}
            </span>
          ) : (
            <ExternalLink to={CreateLinkTo.designerNew(author)} external target="_self" itemProp="url">
              <span itemProp="name">
                {`${author.name || 'unnamed'} ${author.surName || 'user'}`}
              </span>
            </ExternalLink>
          )}
          {index !== authors.length - 1 && (
            <span>
              {', '}
            </span>
          )}
        </ProjectDesignerWrapper>
      ));
    },
    [history, project],
  );

  const curatorsList = useMemo(() =>
    (project?.curators || []).map((author, index) => (
      <ProjectDesignerWrapper
        notLink={isOnlines}
        key={author.designerId}
        alwaysBlack={variant === "bottom"}
      >
        {(isOnlines || fireClick) ? (
          <span>
            {`${author!.name} ${author!.surName}`}
          </span>
        ) : (
          <ExternalLink to={CreateLinkTo.designerNew(author)} external target="_self">
            {`${author!.name} ${author!.surName}`}
          </ExternalLink>
        )}
        {index !== project.curators.length - 1 && (
          <span>
            {', '}
          </span>
        )}
      </ProjectDesignerWrapper>
    )),
    [history, project, fireClick, isOnlines]
  );

  return project ? (
    <ProjectHeaderRoot 
      className="ProjectHeaderRoot" 
      align="center" 
      isProjectPage={isProjectPage}
      isBookPage={isBookPage}
    >
      {authorsList}

      {curatorsList.length > 0 &&
        <span className="ProjectHeaderMadeByOuter">
          <MadeBy className="ProjectHeaderMadeBy">
            {curatorsList.length === 1 ? t('curatedBy_one') : t('curatedBy_other')}
          </MadeBy>
          {curatorsList}
        </span>
      }
    </ProjectHeaderRoot>
  ) : null;
};

export default memo(ProjectHeader)
import React, { FC, memo, useMemo } from 'react';
import { Colors, FInter, FlexBox, ExternalLink } from '@brg/ui';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { Project } from '@brg/api';
import { CreateLinkTo } from '../../../constants/linkTo';
import { LayoutConst } from "../../../../core";
import { AppTester } from 'supporting';

const ProjectDesignerWrapper = styled(FInter.Text) <{ notLink?: boolean, alwaysBlack?: boolean }>`
  font-size: inherit;
  line-height: inherit;
  cursor: ${p => p?.notLink ? 'default' : 'pointer'};
  transition: all 0.2s;
  font-weight: 600;
  color: ${({ theme }) => theme.projectView.authorText};

  ${({ notLink, alwaysBlack, theme }) => {
    if (!notLink) {
      return css`
        &:hover > a {
          color: ${alwaysBlack ? Colors.galBlack100 : theme.projectView.authorColorHover} !important;
        }
      `
    }
  }};
`;

export const ProjectHeaderRoot = styled(FlexBox) <{ isProjectPage?: boolean, isBookPage?: boolean }>`
  display: block;
  margin-bottom: 16px;
  font-size: ${({ theme }) => theme.navigationBar.font.m.fontSize};
  line-height: ${({ theme }) => theme.navigationBar.font.m.lineHeight};
  white-space: break-spaces;

  a {
    color: ${({ theme }) => theme.projectView.authorText};
    transition: color 0.2s;

    &:hover {
      color: ${(p) => p.theme.projectView.authorColorHover};
      transition: color 0.2s;
    }
  }

  @media (max-width: ${LayoutConst.screen.l_16}px) {
    font-size: ${({ theme }) => theme.navigationBar.font.m.fontSize};
    line-height: ${({ theme }) => theme.navigationBar.font.m.lineHeight};

    .ProjectHeaderMadeBy {
      font-size: ${({ theme }) => theme.navigationBar.font.m.fontSize};
      line-height: ${({ theme }) => theme.navigationBar.font.m.lineHeight};
    }
  }

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: ${({ theme }) => theme.navigationBar.font.s.fontSize};
    line-height: ${({ theme }) => theme.navigationBar.font.s.lineHeight};

    .ProjectHeaderMadeBy {
      font-size: ${({ theme }) => theme.navigationBar.font.s.fontSize};
      line-height: ${({ theme }) => theme.navigationBar.font.s.lineHeight};
    }
  }

  ${({ isBookPage }) => isBookPage ? css`
    @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
      max-width: 888px;
      margin: 0 auto 16px auto;
      width: 100%;
    }

    @media (max-width: ${LayoutConst.screen.l_12}px) {
      max-width: 100%;
    }
  ` : null};

  @media (max-width: 940px) {
    margin-bottom: 0;
    // margin-top: 16px;
    padding: 0 16px 0 0;

    * {
      font-weight: 700;
      font-size: 13px;
      line-height: 16px;
    }

    .ProjectHeaderMadeBy {
      font-size: 13px;
      line-height: 16px;
    }
  }

  ${({ isProjectPage }) => isProjectPage ? css`
    @media (max-width: ${LayoutConst.screen.md}px) {
      padding: 0 16px 0 16px;
    }

    @media (max-width: ${LayoutConst.screen.s - 1}px) {
      padding: 0 10px 0 0;
      padding-top: 0;
    }
  ` : null};

  span {
    @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
      font-size: inherit;
      line-height: inherit;
    }

    @media (max-width: 940px) {
      font-size: inherit;
      line-height: inherit;
    }
  }

  .ProjectHeaderMadeByOuter {
    margin-left: 5px;
  }
`;

const MadeBy = styled(FInter.Text)`
  font-size: ${({ theme }) => theme.navigationBar.font.m.fontSize};
  line-height: ${({ theme }) => theme.navigationBar.font.m.lineHeight};
  margin-right: 5px;
  color: ${({ theme }) => theme.projectView.authorCuratorText};
`;

type PropsT = {
  project: Project;
  fireClick?: boolean;
  isProjectPage?: boolean;
  isBookPage?: boolean;
  variant?: string;
};

const ProjectHeader: FC<PropsT> = ({ project, fireClick, isProjectPage, isBookPage, variant }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const isOnlines = AppTester.isOnlines();

  const authorsList = useMemo(
    () => {
      const authors = (project?.author ? [project?.author] : []).concat(project?.coAuthors || []);
      return (authors).map((author, index) => (
        <ProjectDesignerWrapper
          key={author.designerId}
          notLink={isOnlines || fireClick}
          alwaysBlack={variant === "bottom"}
          itemProp="author" itemScope itemType="https://schema.org/Person"
        >
          {(isOnlines || fireClick) ? (
            <span itemProp="name">
              {`${author.name || 'unnamed'} ${author.surName || 'user'}`}
            </span>
          ) : (
            <ExternalLink to={CreateLinkTo.designerNew(author)} external target="_self" itemProp="url">
              <span itemProp="name">
                {`${author.name || 'unnamed'} ${author.surName || 'user'}`}
              </span>
            </ExternalLink>
          )}
          {index !== authors.length - 1 && (
            <span>
              {', '}
            </span>
          )}
        </ProjectDesignerWrapper>
      ));
    },
    [history, project],
  );

  const curatorsList = useMemo(() =>
    (project?.curators || []).map((author, index) => (
      <ProjectDesignerWrapper
        notLink={isOnlines}
        key={author.designerId}
        alwaysBlack={variant === "bottom"}
      >
        {(isOnlines || fireClick) ? (
          <span>
            {`${author!.name} ${author!.surName}`}
          </span>
        ) : (
          <ExternalLink to={CreateLinkTo.designerNew(author)} external target="_self">
            {`${author!.name} ${author!.surName}`}
          </ExternalLink>
        )}
        {index !== project.curators.length - 1 && (
          <span>
            {', '}
          </span>
        )}
      </ProjectDesignerWrapper>
    )),
    [history, project, fireClick, isOnlines]
  );

  return project ? (
    <ProjectHeaderRoot 
      className="ProjectHeaderRoot" 
      align="center" 
      isProjectPage={isProjectPage}
      isBookPage={isBookPage}
    >
      {authorsList}

      {curatorsList.length > 0 &&
        <span className="ProjectHeaderMadeByOuter">
          <MadeBy className="ProjectHeaderMadeBy">
            {curatorsList.length === 1 ? t('curatedBy_one') : t('curatedBy_other')}
          </MadeBy>
          {curatorsList}
        </span>
      }
    </ProjectHeaderRoot>
  ) : null;
};

export default memo(ProjectHeader)
